import { Box, CircularProgress } from '@mui/material';
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAppMessaging } from '../../utils/app-messaging/use-app-messaging';
import { useAuth } from '../../utils/auth/hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  unauthenticatedPath: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  unauthenticatedPath,
}) => {
  const { accessToken, isLoading } = useAuth();
  const location = useLocation();
  const { mode, send } = useAppMessaging();

  useEffect(() => {
    // Only embedded apps announce readiness. In parent mode this used to post
    // app-ready to the shell's own window and prematurely flip its child-ready
    // state (specs/016-global-nav-venue-sync/contracts/shell-app-messaging.md).
    if (mode !== 'child') {
      return;
    }

    send({ type: 'app-ready' });
  }, [mode, send]);

  if (isLoading && !accessToken) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!accessToken && !isLoading) {
    // Redirect to login with the current location as return path
    const returnTo = location.pathname + location.search;
    return (
      <Navigate to={`${unauthenticatedPath}?returnTo=${encodeURIComponent(returnTo)}`} replace />
    );
  }

  return <>{children}</>;
};
